import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../utils/toast";
import dynamicSurveyService, { DynamicSurveyDto } from "../../services/forms/dynamic.service";

const DynamikAnketYonetim = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["adminDynamicSurveys"],
    queryFn: () => dynamicSurveyService.getSurveys({ params: { include_inactive: true } }),
  });

  const surveys: DynamicSurveyDto[] = data?.result?.data ?? [];

  const handleDelete = (survey: DynamicSurveyDto) => {
    confirmDialog({
      message: `"${survey.title}" anketini kaldırmak istediğinize emin misiniz? Anket yayından ve listeden tamamen kaldırılacaktır.`,
      header: "Anketi Sil / Kaldır",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet, Kaldır",
      rejectLabel: "İptal",
      acceptClassName: "p-button-danger",
      accept: async () => {
        const res = await dynamicSurveyService.deleteSurvey(survey.id!);
        if (res.result && !res.result.error) {
          toast.show("Anket listeden ve yayından kaldırıldı.", "success");
          queryClient.invalidateQueries({ queryKey: ["adminDynamicSurveys"] });
        } else {
          toast.show("İşlem başarısız.", "error");
        }
      },
    });
  };

  const statusBody = (survey: DynamicSurveyDto) => (
    <Tag value={survey.is_active ? "Aktif" : "Pasif"} severity={survey.is_active ? "success" : undefined} />
  );

  const actionsBody = (survey: DynamicSurveyDto) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-chart-bar"
        rounded text severity="info"
        tooltip="Analiz" tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/admin/dinamik-anket-analiz/${survey.id}`)}
      />
      <Button
        icon="pi pi-pencil"
        rounded text severity="warning"
        tooltip="Düzenle" tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/admin/dinamik-anket-olustur/${survey.id}`)}
      />
      <Button
        icon="pi pi-trash"
        rounded text severity="danger"
        tooltip="Sil" tooltipOptions={{ position: "top" }}
        onClick={() => handleDelete(survey)}
      />
    </div>
  );

  return (
    <div className="p-3 md:p-4">
      <div className="flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="text-900 font-bold text-2xl m-0">Anket Yönetimi</h2>
          <p className="text-500 mt-1 mb-0">Dinamik anketleri oluşturun, düzenleyin ve yönetin.</p>
        </div>
        <Button label="Yeni Anket" icon="pi pi-plus" onClick={() => navigate("/admin/dinamik-anket-olustur")} />
      </div>

      {isLoading ? (
        <div className="flex justify-content-center p-6">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            value={surveys}
            stripedRows
            className="shadow-1 border-round"
            emptyMessage="Henüz anket oluşturulmamış."
            tableStyle={{ minWidth: "560px" }}
          >
            <Column field="title" header="Başlık" />
            <Column
              header="Sorular"
              body={(s: DynamicSurveyDto) => `${s.questions?.length ?? 0} soru`}
              style={{ width: "90px" }}
            />
            <Column
              header="Yanıt / Kontenjan"
              body={(s: DynamicSurveyDto) => (
                <div className="flex align-items-center gap-1">
                  <span className="font-semibold">{s.response_count ?? 0}</span>
                  <span className="text-500">/</span>
                  <span>{s.response_limit ? s.response_limit : "∞"}</span>
                  {s.closure_reason === "limit_reached" && (
                    <Tag value="Doldu" severity="danger" style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem" }} />
                  )}
                </div>
              )}
              style={{ width: "140px" }}
            />
            <Column header="Durum" body={statusBody} style={{ width: "90px" }} />
            <Column
              header="Oluşturulma"
              body={(s: DynamicSurveyDto) => s.created_at ? new Date(s.created_at).toLocaleDateString("tr-TR") : "-"}
              style={{ width: "120px" }}
            />
            <Column header="İşlemler" body={actionsBody} style={{ width: "120px" }} />
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default DynamikAnketYonetim;
